import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, ArrowLeft } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function HunterOrders({ user }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const customerInputRef = useRef(null);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [newOrder, setNewOrder] = useState({
    customer_name: "",
    payment_method: "Bar",
    items: [],
    status: "Offen"
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [editMode, setEditMode] = useState(false); // Bearbeitungsmodus für hunter_editor
  const [deleteDialog, setDeleteDialog] = useState({ open: false, orderId: null, orderName: "" });

  // Check if user has hunter_editor permission
  const isHunterEditor = user.permissions?.includes("hunter_editor") || false;

  useEffect(() => {
    loadOrders();
    loadInventory();
    
    // Auto-refresh every 10 seconds for live updates
    const intervalId = setInterval(() => {
      loadOrders();
      loadInventory();
    }, 10000); // 10 seconds
    
    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  // Fix text selection when editing order
  useEffect(() => {
    if (showOrderDialog && editingOrder) {
      // Multiple attempts to fix the selection
      const fixSelection = () => {
        const input = customerInputRef.current;
        if (input && input.value) {
          const len = input.value.length;
          input.setSelectionRange(len, len);
        }
      };
      
      // Try at different times
      setTimeout(fixSelection, 50);
      setTimeout(fixSelection, 150);
      setTimeout(fixSelection, 300);
      setTimeout(fixSelection, 500);
    }
  }, [showOrderDialog, editingOrder]);

  const loadOrders = async () => {
    try {
      const res = await axios.get(`${API}/orders`);
      setOrders(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Bestellungen");
    }
  };

  const loadInventory = async () => {
    try {
      const res = await axios.get(`${API}/inventory`);
      setInventory(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden des Inventars");
    }
  };

  const openNewOrderDialog = () => {
    setEditingOrder(null);
    setNewOrder({
      customer_name: "",
      payment_method: "Bar",
      items: [],
      status: "Offen"
    });
    setSelectedItem(null);
    setItemQuantity(1);
    setShowOrderDialog(true);
  };

  const openEditOrderDialog = (order) => {
    setEditingOrder(order);
    setNewOrder({
      customer_name: order.customer_name,
      payment_method: order.payment_method,
      items: [...order.items],
      status: order.status
    });
    setSelectedItem(null);
    setItemQuantity(1);
    setShowOrderDialog(true);
  };

  const addItemToOrder = () => {
    if (!selectedItem || itemQuantity <= 0) {
      toast.error("Bitte wählen Sie ein Item und geben Sie eine Menge ein");
      return;
    }

    const item = inventory.find(i => i.id === selectedItem);
    if (!item) return;

    const available = item.stock - (item.reserved || 0);
    if (available < itemQuantity) {
      toast.error(`Nur ${available} verfügbar (${item.reserved || 0} vorgemerkt)`);
      return;
    }

    const existingItemIndex = newOrder.items.findIndex(i => i.item_id === item.id);
    
    if (existingItemIndex >= 0) {
      const updatedItems = [...newOrder.items];
      updatedItems[existingItemIndex].quantity += itemQuantity;
      updatedItems[existingItemIndex].total_price = updatedItems[existingItemIndex].quantity * item.price;
      setNewOrder({...newOrder, items: updatedItems});
    } else {
      const orderItem = {
        item_id: item.id,
        item_name: item.name,
        quantity: itemQuantity,
        price_per_unit: item.price,
        total_price: item.price * itemQuantity
      };
      setNewOrder({...newOrder, items: [...newOrder.items, orderItem]});
    }

    setSelectedItem(null);
    setItemQuantity(1);
  };

  const removeItemFromOrder = (itemId) => {
    setNewOrder({...newOrder, items: newOrder.items.filter(i => i.item_id !== itemId)});
  };

  const updateItemQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) return;
    
    const updatedItems = newOrder.items.map(item => {
      if (item.item_id === itemId) {
        return {
          ...item,
          quantity: newQuantity,
          total_price: item.price_per_unit * newQuantity
        };
      }
      return item;
    });
    setNewOrder({...newOrder, items: updatedItems});
  };

  const handleSaveOrder = async () => {
    if (!newOrder.customer_name.trim()) {
      toast.error("Bitte geben Sie einen Kundennamen ein");
      return;
    }

    if (newOrder.items.length === 0) {
      toast.error("Bitte fügen Sie mindestens ein Item hinzu");
      return;
    }

    try {
      if (editingOrder) {
        await axios.put(`${API}/orders/${editingOrder.id}`, {
          customer_name: newOrder.customer_name,
          payment_method: newOrder.payment_method,
          items: newOrder.items,
          status: newOrder.status
        });
        toast.success("Bestellung aktualisiert");
      } else {
        await axios.post(`${API}/orders`, newOrder);
        toast.success("Bestellung erstellt");
      }
      
      setShowOrderDialog(false);
      loadOrders();
      loadInventory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status: newStatus });
      toast.success("Status aktualisiert");
      loadOrders();
      loadInventory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Aktualisieren");
    }
  };

  const openDeleteDialog = (orderId, orderName) => {
    setDeleteDialog({ open: true, orderId, orderName });
  };

  const handleDeleteOrder = async () => {
    try {
      await axios.delete(`${API}/orders/${deleteDialog.orderId}`);
      toast.success("Bestellung gelöscht");
      setDeleteDialog({ open: false, orderId: null, orderName: "" });
      loadOrders();
      loadInventory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const calculateOrderTotal = () => {
    return newOrder.items.reduce((sum, item) => sum + item.total_price, 0).toFixed(2);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Offen": return "#fbbf24"; // yellow
      case "In Bearbeitung": return "#60a5fa"; // blue
      case "Abgeschlossen": return "#22c55e"; // green
      case "Storniert": return "#ef4444"; // red
      default: return "#6d5838";
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto p-8">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => navigate('/hunter')} 
            className="rdr-button"
            style={{ background: '#6d5838' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Zurück zum Hunter
          </Button>
          <h2 className="text-3xl font-bold" style={{ color: '#f4e8d0' }}>Bestellungen</h2>
        </div>
        <div className="flex gap-3">
          <Button onClick={openNewOrderDialog} className="rdr-button">
            <Plus size={16} className="mr-2" />
            Neue Bestellung
          </Button>
          {isHunterEditor && (
            <Button
              onClick={() => setEditMode(!editMode)}
              className="rdr-button"
              style={{ 
                background: editMode ? '#8b7355' : '#3d2f1f',
                color: '#f4e8d0'
              }}
            >
              {editMode ? '✓ Bearbeitungsmodus aktiv' : 'Bearbeitungsmodus'}
            </Button>
          )}
        </div>
      </div>

      {/* Orders List */}
      <div className="rdr-card">
        <div className="space-y-3">
          {orders.length === 0 ? (
            <p className="text-center py-8" style={{ color: '#6d5838' }}>Keine Bestellungen vorhanden</p>
          ) : (
            orders.map((order) => {
              // Check if current user can edit this order
              const canEdit = 
                order.created_by_id === user.id || 
                isHunterEditor || 
                user.is_admin;
              
              return (
              <div key={order.id} className="p-4 rounded" style={{ background: 'rgba(244, 232, 208, 0.5)' }}>
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-bold text-lg" style={{ color: '#3d2f1f' }}>
                        {order.customer_name}
                      </h4>
                      <span 
                        className="text-xs px-3 py-1 rounded font-bold"
                        style={{ background: getStatusColor(order.status), color: '#fff' }}
                      >
                        {order.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p style={{ color: '#6d5838' }}>
                        <strong>Datum:</strong> {new Date(order.order_date).toLocaleString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      <p style={{ color: '#6d5838' }}>
                        <strong>Bearbeiter:</strong> {order.created_by_display || order.created_by}
                      </p>
                      <p style={{ color: '#6d5838' }}>
                        <strong>Zahlung:</strong> {order.payment_method}
                      </p>
                      <p style={{ color: '#6d5838' }}>
                        <strong>Summe:</strong> ${order.total_amount.toFixed(2)}
                      </p>
                    </div>
                    <div className="mt-2">
                      <p className="text-xs font-bold" style={{ color: '#3d2f1f' }}>Items:</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {order.items.map((item, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 rounded" style={{ background: '#8b7355', color: '#f4e8d0' }}>
                            {item.quantity}x {item.item_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Fixed width button container for consistent layout */}
                  <div className="flex gap-2" style={{ minWidth: '160px', justifyContent: 'flex-end' }}>
                    {/* Bearbeiten nur für Ersteller, hunter_editor oder admin */}
                    {canEdit && (order.status !== "Abgeschlossen" || isHunterEditor || user.is_admin) && (
                      <button
                        onClick={() => openEditOrderDialog(order)}
                        className="px-3 py-1 rounded rdr-button text-sm"
                        title="Bearbeiten"
                      >
                        Bearbeiten
                      </button>
                    )}
                    {(isHunterEditor && editMode) && (
                      <button
                        onClick={() => openDeleteDialog(order.id, `Bestellung: ${order.customer_name}`)}
                        className="p-2 rounded hover:bg-[#8b7355] hover:bg-opacity-30"
                        title="Löschen"
                      >
                        <Trash2 size={16} style={{ color: '#8b4513' }} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>

      {/* Order Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent 
          className="rdr-card border-4 border-[#8b7355] max-w-3xl max-h-[90vh] overflow-y-auto"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            margin: 0
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ color: '#3d2f1f' }}>
              {editingOrder ? 'Bestellung bearbeiten' : 'Neue Bestellung'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Customer Name */}
            <div>
              <Label style={{ color: '#3d2f1f', fontWeight: 600 }}>Kunde</Label>
              <Input
                ref={customerInputRef}
                value={newOrder.customer_name}
                onChange={(e) => setNewOrder({...newOrder, customer_name: e.target.value})}
                onFocus={(e) => {
                  // Remove text selection when field receives focus
                  if (editingOrder) {
                    e.target.setSelectionRange(e.target.value.length, e.target.value.length);
                  }
                }}
                className="rdr-input"
                placeholder="Name des Kunden"
                autoComplete="off"
              />
            </div>

            {/* Payment Method */}
            <div>
              <Label style={{ color: '#3d2f1f', fontWeight: 600 }}>Zahlungsmethode</Label>
              <Select
                value={newOrder.payment_method}
                onValueChange={(value) => setNewOrder({...newOrder, payment_method: value})}
              >
                <SelectTrigger className="rdr-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bar">Bar</SelectItem>
                  <SelectItem value="Rechnung">Rechnung</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status (only when editing) */}
            {editingOrder && (
              <div>
                <Label style={{ color: '#3d2f1f', fontWeight: 600 }}>Status</Label>
                <Select
                  value={newOrder.status}
                  onValueChange={(value) => setNewOrder({...newOrder, status: value})}
                >
                  <SelectTrigger className="rdr-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Offen">Offen</SelectItem>
                    <SelectItem value="In Bearbeitung">In Bearbeitung</SelectItem>
                    <SelectItem value="Abgeschlossen">Abgeschlossen</SelectItem>
                    <SelectItem value="Storniert">Storniert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Add Items */}
            <div className="border-t pt-4" style={{ borderColor: '#8b7355' }}>
              <Label style={{ color: '#3d2f1f', fontWeight: 600 }}>Items hinzufügen</Label>
              <div className="flex gap-2 mt-2">
                <Select value={selectedItem} onValueChange={setSelectedItem}>
                  <SelectTrigger className="rdr-input flex-1">
                    <SelectValue placeholder="Item auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {inventory.map((item) => {
                      const available = item.stock - (item.reserved || 0);
                      return (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name} - ${item.price} (Verfügbar: {available})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min="1"
                  value={itemQuantity}
                  onChange={(e) => setItemQuantity(parseInt(e.target.value) || 1)}
                  className="rdr-input w-24"
                  placeholder="Menge"
                />
                <Button onClick={addItemToOrder} className="rdr-button">
                  <Plus size={16} />
                </Button>
              </div>
            </div>

            {/* Order Items List */}
            {newOrder.items.length > 0 && (
              <div className="border-t pt-4" style={{ borderColor: '#8b7355' }}>
                <Label style={{ color: '#3d2f1f', fontWeight: 600 }}>Bestellte Items</Label>
                <div className="space-y-2 mt-2">
                  {newOrder.items.map((item) => (
                    <div key={item.item_id} className="flex items-center justify-between p-3 rounded" style={{ background: 'rgba(139, 115, 85, 0.15)' }}>
                      <div className="flex-1">
                        <p className="font-bold" style={{ color: '#3d2f1f' }}>{item.item_name}</p>
                        <p className="text-sm" style={{ color: '#6d5838' }}>
                          ${item.price_per_unit} × {item.quantity} = ${item.total_price.toFixed(2)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItemQuantity(item.item_id, parseInt(e.target.value) || 1)}
                          className="rdr-input w-20"
                        />
                        <button
                          onClick={() => removeItemFromOrder(item.item_id)}
                          className="p-2 rounded hover:bg-[#8b7355] hover:bg-opacity-30"
                        >
                          <Trash2 size={16} style={{ color: '#8b4513' }} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 rounded text-right" style={{ background: 'rgba(139, 115, 85, 0.3)' }}>
                  <p className="text-xl font-bold" style={{ color: '#3d2f1f' }}>
                    Gesamt: ${calculateOrderTotal()}
                  </p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 justify-end pt-4">
              <Button onClick={() => setShowOrderDialog(false)} className="rdr-button">
                Abbrechen
              </Button>
              <Button onClick={handleSaveOrder} className="rdr-button" style={{ background: '#22c55e' }}>
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, orderId: null, orderName: "" })}>
        <AlertDialogContent className="rdr-card border-4 border-[#8b7355]" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', maxWidth: '500px' }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold" style={{ color: '#3d2f1f', fontFamily: 'Crimson Text, serif' }}>
              Bestellung löschen?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base" style={{ color: '#6d5838' }}>
              Möchten Sie "{deleteDialog.orderName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <AlertDialogCancel className="rdr-button" style={{ background: 'linear-gradient(to bottom, #6d5838 0%, #5a4a3a 100%)' }}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteOrder}
              className="rdr-button" 
              style={{ background: 'linear-gradient(to bottom, #8b4513 0%, #6b3410 100%)' }}
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
